import React, { useEffect, useState, useRef, useCallback } from 'react';
import { customFetch } from '../apiClient'; // Path apne project structure ke hisaab se adjust kar lein

const BankDTForm = () => {
  const [banks, setBanks] = useState([]);
  const [editRowId, setEditRowId] = useState(null);
  const [editForm, setEditForm] = useState({
    bName: "", acNo: "", ifsc: "", opnAmt: "",
  });
  const [newForm, setNewForm] = useState({
    bName: "", acNo: "", ifsc: "", opnAmt: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const formRef = useRef(null);

  // Auto-dismiss alerts
  useEffect(() => {
    if (success) { const timer = setTimeout(() => setSuccess(""), 3000); return () => clearTimeout(timer); }
  }, [success]);

  useEffect(() => {
    if (error) { const timer = setTimeout(() => setError(""), 4000); return () => clearTimeout(timer); }
  }, [error]);

  const fetchBanks = async () => {
    try {
      const res = await customFetch('/BankDT');
      if (!res.ok) throw new Error("Failed to load bank data");
      const data = await res.json();
      setBanks(data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => { fetchBanks(); }, []);

  // Auto-focus first input
  useEffect(() => {
    if (formRef.current) {
      const firstInput = formRef.current.querySelector('input:not([disabled]):not([type="hidden"])');
      firstInput?.focus();
    }
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    const focusableElements = formRef.current?.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
    );
    if (!focusableElements || focusableElements.length === 0) return;
    const currentIndex = Array.from(focusableElements).indexOf(e.target);

    if (e.key === 'Enter' && currentIndex === focusableElements.length - 1) {
      e.target.closest('form').requestSubmit(); return;
    }
    if (['Enter', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = Math.max(0, currentIndex - 1);
      focusableElements[prevIndex]?.focus();
    }
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (formRef.current && e.target.closest('form') === formRef.current) {
        handleKeyDown(e);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  const handleEditClick = (bank) => {
    setEditRowId(bank.bid);
    setEditForm({
      bName: bank.bName, acNo: bank.acNo, ifsc: bank.ifsc, opnAmt: bank.opnAmt, bid: bank.bid,
    });
    setError(""); setSuccess("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewInputChange = (e) => {
    const { name, value } = e.target;
    setNewForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateSubmit = async (bid) => {
    if (!editForm.bName.trim() || !editForm.acNo.trim()) { setError("Bank Name & Acc No required."); return; }
    setLoading(true); setError(""); setSuccess("");

    try {
      const payload = { ...editForm, bid };
      const res = await customFetch(`/BankDT/${bid}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text() || "Update failed.");
      setSuccess("Bank updated successfully!"); 
      setEditRowId(null); 
      fetchBanks();
    } catch (err) { 
      setError(err.message); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleDelete = async (bid) => {
    if (!window.confirm("Delete this bank?")) return;
    setLoading(true); setError(""); setSuccess("");

    try {
      const res = await customFetch(`/BankDT/${bid}`, {
        method: "DELETE"
      });
      if (!res.ok) throw new Error(await res.text() || "Delete failed.");
      setSuccess("Bank deleted successfully!");
      if (editRowId === bid) setEditRowId(null);
      fetchBanks();
    } catch (err) { 
      setError(err.message); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleAddNewSubmit = async (e) => {
    e.preventDefault();
    if (!newForm.bName.trim() || !newForm.acNo.trim()) { setError("Bank Name & Acc No required."); return; }
    setLoading(true); setError(""); setSuccess("");

    try {
      const res = await customFetch('/BankDT', {
        method: "POST",
        body: JSON.stringify(newForm),
      });
      if (!res.ok) throw new Error(await res.text() || "Add failed.");
      setSuccess("Bank added successfully!");
      setNewForm({ bName: "", acNo: "", ifsc: "", opnAmt: "" });
      fetchBanks();
    } catch (err) { 
      setError(err.message); 
    } finally { 
      setLoading(false); 
    }
  };

  return (
    <div className="dc-wrap">
      
      {/* Add Bank Card */}
      <div className="dc-card dc-mb3">
        <div className="dc-header">
          <div className="dc-header-inner">
            <div className="dc-header-brand">
              <i className="bi bi-bank"></i> <span>Add New Bank</span>
            </div>
          </div>
        </div>

        <div className="dc-body">
          {error && <div className="dc-alert dc-alert-danger"><i className="bi bi-exclamation-triangle-fill"></i> {error}</div>}
          {success && <div className="dc-alert dc-alert-success"><i className="bi bi-check-circle-fill"></i> {success}</div>}

          <form ref={formRef} onSubmit={handleAddNewSubmit} className="dc-inline-form">
            <div className="dc-inline-input" style={{ flex: 1.5 }}>
              <label className="dc-label">Bank Name <span className="dc-required">*</span></label>
              <input name="bName" type="text" className="dc-input" placeholder="e.g. SBI" value={newForm.bName} onChange={handleNewInputChange} disabled={loading} required />
            </div>
            <div className="dc-inline-input">
              <label className="dc-label">Account Number <span className="dc-required">*</span></label>
              <input name="acNo" type="text" className="dc-input" placeholder="1234567890" value={newForm.acNo} onChange={handleNewInputChange} disabled={loading} required />
            </div>
            <div className="dc-inline-input">
              <label className="dc-label">IFSC Code <span className="dc-required">*</span></label>
              <input name="ifsc" type="text" className="dc-input" placeholder="SBIN0001234" value={newForm.ifsc} onChange={handleNewInputChange} disabled={loading} required />
            </div>
            <div className="dc-inline-input" style={{ maxWidth: '120px' }}>
              <label className="dc-label">Opening Amt</label>
              <input name="opnAmt" type="number" step="0.01" className="dc-input dc-text-right" placeholder="0.00" value={newForm.opnAmt} onChange={handleNewInputChange} disabled={loading} />
            </div>
            <button type="submit" className="dc-btn-submit" disabled={loading}>
              <i className="bi bi-plus-lg"></i> {loading ? "..." : "Add Bank"}
            </button>
          </form>
        </div>
      </div>

      {/* Bank List Card */}
      <div className="dc-card">
        <div className="dc-header">
          <div className="dc-header-inner">
            <div className="dc-header-brand">
              <i className="bi bi-list-ul"></i> <span>Bank Master List ({banks.length})</span>
            </div>
          </div>
        </div>

        <div className="dc-body">
          <div className="dc-table-wrap">
            <table className="dc-table">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>ID</th>
                  <th>Bank Name</th>
                  <th>Account No</th>
                  <th>IFSC</th>
                  <th className="dc-text-right">Opn Amt</th>
                  <th className="dc-text-center" style={{ width: '90px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {banks.length === 0 ? (
                  <tr><td colSpan="6" className="dc-empty">No banks found.</td></tr>
                ) : (
                  banks.map((bank) => (
                    <tr key={bank.bid}>
                      <td className="dc-text-muted">{bank.bid}</td>
                      <td>
                        {editRowId === bank.bid ? (
                          <input type="text" name="bName" value={editForm.bName} onChange={handleInputChange} className="dc-input" disabled={loading} autoFocus />
                        ) : bank.bName}
                      </td>
                      <td>
                        {editRowId === bank.bid ? (
                          <input type="text" name="acNo" value={editForm.acNo} onChange={handleInputChange} className="dc-input" disabled={loading} />
                        ) : bank.acNo}
                      </td>
                      <td>
                        {editRowId === bank.bid ? (
                          <input type="text" name="ifsc" value={editForm.ifsc} onChange={handleInputChange} className="dc-input" disabled={loading} />
                        ) : bank.ifsc}
                      </td>
                      <td className="dc-text-right">
                        {editRowId === bank.bid ? (
                          <input type="number" name="opnAmt" step="0.01" value={editForm.opnAmt} onChange={handleInputChange} className="dc-input dc-text-right" disabled={loading} />
                        ) : `₹${Number(bank.opnAmt || 0).toFixed(2)}`}
                      </td>
                      <td className="dc-text-center">
                        {editRowId === bank.bid ? (
                          <div className="dc-btn-group">
                            <button className="dc-icon-btn dc-save" onClick={() => handleUpdateSubmit(bank.bid)} disabled={loading}><i className="bi bi-check-lg"></i></button>
                            <button className="dc-icon-btn dc-cancel" onClick={() => setEditRowId(null)} disabled={loading}><i className="bi bi-x-lg"></i></button>
                          </div>
                        ) : (
                          <div className="dc-btn-group">
                            <button className="dc-icon-btn dc-edit" onClick={() => handleEditClick(bank)}><i className="bi bi-pencil-square"></i></button>
                            <button className="dc-icon-btn dc-delete" onClick={() => handleDelete(bank.bid)}><i className="bi bi-trash"></i></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style jsx>{`
        *, *::before, *::after { box-sizing: border-box; }
        .dc-wrap { padding: 0; font-family: system-ui, sans-serif; }
        
        .dc-card { background: #fff; border-radius: 6px; box-shadow: 0 1px 8px rgba(0,0,0,0.06); border: 1px solid #e0e6ed; overflow: hidden; }
        .dc-mb3 { margin-bottom: 12px; }
        
        .dc-header { background: var(--theme-color, #0088cc); padding: 6px 12px; border-bottom: 2px solid rgba(0,0,0,0.1); }
        .dc-header-inner { display: flex; align-items: center; }
        .dc-header-brand { display: flex; align-items: center; gap: 6px; color: white; font-weight: 600; font-size: 13px; }
        
        .dc-body { padding: 10px 12px; }

        .dc-inline-form { display: flex; gap: 8px; align-items: flex-end; flex-wrap: wrap; }
        .dc-inline-input { flex: 1; min-width: 120px; display: flex; flex-direction: column; }
        
        .dc-label { display: block; font-size: 10px; font-weight: 600; color: #2c3e50; margin-bottom: 2px; }
        .dc-required { color: #dc2626; }
        
        .dc-input { width: 100%; height: 26px; min-height: 26px; padding: 2px 6px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; outline: none; }
        .dc-input:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0,136,204,0.15); }

        .dc-btn-submit { height: 26px; padding: 0 14px; background: var(--theme-color, #0088cc); color: white; border: none; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px; white-space: nowrap; }
        .dc-btn-submit:hover { opacity: 0.9; }
        .dc-btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

        .dc-table-wrap { overflow-x: auto; border: 1px solid #e0e6ed; border-radius: 4px; }
        .dc-table { width: 100%; border-collapse: collapse; font-size: 11px; min-width: 600px; }
        .dc-table th { background: var(--theme-color, #0088cc); color: white; padding: 6px 8px; font-weight: 600; text-align: left; }
        .dc-table td { padding: 5px 8px; border-bottom: 1px solid #e0e6ed; vertical-align: middle; }
        .dc-table tr:hover { background: #f0f7ff; }
        
        .dc-text-center { text-align: center; }
        .dc-text-right { text-align: right; }
        .dc-text-muted { color: #6c757d; }
        .dc-empty { text-align: center; padding: 20px; color: #7f8c8d; }

        .dc-btn-group { display: flex; gap: 4px; justify-content: center; }
        .dc-icon-btn { width: 22px; height: 22px; border-radius: 3px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; }
        .dc-edit { background: #e0f2fe; color: #0284c7; }
        .dc-edit:hover { background: #0284c7; color: white; }
        .dc-delete { background: #fee2e2; color: #dc2626; }
        .dc-delete:hover { background: #dc2626; color: white; }
        .dc-save { background: #d1fae5; color: #059669; }
        .dc-save:hover { background: #059669; color: white; }
        .dc-cancel { background: #f3f4f6; color: #495057; }
        .dc-cancel:hover { background: #cbd5e0; }

        .dc-alert { padding: 6px 10px; border-radius: 4px; font-size: 11px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .dc-alert-danger { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
        .dc-alert-success { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }

        @media (max-width: 600px) {
          .dc-inline-form { flex-direction: column; align-items: stretch; }
          .dc-inline-input { max-width: 100% !important; }
          .dc-btn-submit { justify-content: center; margin-top: 4px; }
        }
      `}</style>
    </div>
  );
};

export default BankDTForm;